function buildQuickAddProductUrl(productUrl, variantId) {
  if (!productUrl || !variantId) return productUrl;
  try {
    const u = new URL(productUrl, window.location.href);
    u.searchParams.set('variant', String(variantId));
    return u.toString();
  } catch (e) {
    const sep = productUrl.includes('?') ? '&' : '?';
    return `${productUrl}${sep}variant=${encodeURIComponent(String(variantId))}`;
  }
}

function quickAddGetSubmitSpinner(button) {
  return button.querySelector('.loading-overlay__spinner') || button.querySelector('.loading__spinner');
}

function quickAddSetButtonLoading(button, loading) {
  const spinner = quickAddGetSubmitSpinner(button);
  if (loading) {
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    if (spinner) spinner.classList.remove('hidden');
  } else {
    button.removeAttribute('aria-disabled');
    button.classList.remove('loading');
    if (spinner) spinner.classList.add('hidden');
  }
}

function quickAddReplaceSubmitWithTypeButton(nativeSubmitBtn) {
  const customBtn = nativeSubmitBtn.cloneNode(true);
  customBtn.type = 'button';
  nativeSubmitBtn.parentNode.replaceChild(customBtn, nativeSubmitBtn);
  return customBtn;
}

function quickAddResolveBundleProperties(variantId, isGiftChecked, cartItems) {
  const properties = {};
  let bundleId = null;
  const existingItem = cartItems.find(
    (item) =>
      String(item.variant_id) === String(variantId) && item.properties && item.properties._BundleId
  );
  if (existingItem) {
    bundleId = existingItem.properties._BundleId;
    properties._BundleId = bundleId;
  } else if (isGiftChecked) {
    bundleId = 'bundle_' + Date.now();
    properties._BundleId = bundleId;
  }
  return { properties, bundleId };
}

async function quickAddPostMainLineItem(variantId, properties) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [{ id: variantId, quantity: 1, properties }],
    }),
  });
  if (!response.ok) throw new Error('Error al agregar');
}

async function quickAddRunPostAddHooks(isGiftChecked, bundleId, giftVariantId) {
  if (isGiftChecked && bundleId && typeof window.addGiftToCart === 'function') {
    await window.addGiftToCart(giftVariantId, bundleId);
  }
  if (window.forceBagCorrection) await window.forceBagCorrection();
  if (typeof window.refreshCartDrawer === 'function') await window.refreshCartDrawer();
}

function quickAddOpenCartDrawer() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer) return;
  if (typeof cartDrawer.open === 'function') cartDrawer.open();
  else cartDrawer.classList.add('active');
}

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      }

      hide(preventFocus = false) {
        this.teardownThumbnailHeightSync();
        this.clearThumbnailRailStyles();
        const cartNotification =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        const openerSpinner =
          opener.querySelector('.loading-overlay__spinner') || opener.querySelector('.loading__spinner');
        if (openerSpinner) openerSpinner.classList.remove('hidden');

        const productUrl = opener.getAttribute('data-product-url');
        if (!productUrl) {
          console.error('Error: El botón no tiene el atributo data-product-url');
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          if (openerSpinner) openerSpinner.classList.add('hidden');
          return;
        }

        const variantId = opener.getAttribute('data-variant-id');
        const fetchUrl = variantId ? buildQuickAddProductUrl(productUrl, variantId) : productUrl;

        fetch(fetchUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');

            if (!this.productElement) return;

            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

            if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.removeGalleryListSemantic();
            this.updateImageSizes();
            this.forceModalGalleryImagesLoad();
            this.preventVariantURLSwitching();
            super.show(opener);

            this.syncModalVariantMedia();
            this.initQuickAddGift();
            this.setupThumbnailHeightSync();
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            const spin =
              opener.querySelector('.loading-overlay__spinner') || opener.querySelector('.loading__spinner');
            if (spin) spin.classList.add('hidden');
          });
      }

      preventDuplicatedIDs() {
        if (!this.productElement?.dataset) return;
        const sectionId = this.productElement.dataset.section;
        if (!sectionId) return;

        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(
          sectionId,
          `quickadd-${sectionId}`
        );
        this.productElement
          .querySelectorAll('variant-selects, variant-radios, product-info')
          .forEach((el) => (el.dataset.originalSection = sectionId));
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;
        element.querySelectorAll('script').forEach((oldScript) => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));
          newScript.appendChild(document.createTextNode(oldScript.innerHTML));
          oldScript.parentNode.replaceChild(newScript, oldScript);
        });
      }

      initQuickAddGift() {
        const modal = this.modalContent;
        const giftCheckbox = modal.querySelector('#gift-checkbox');
        const nativeForm = modal.querySelector('product-form form');
        const nativeSubmitBtn = modal.querySelector('[name="add"]');

        if (!nativeSubmitBtn || !nativeForm || !giftCheckbox) return;

        const customBtn = quickAddReplaceSubmitWithTypeButton(nativeSubmitBtn);
        const modalInstance = this;

        customBtn.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();

          quickAddSetButtonLoading(customBtn, true);

          try {
            const variantId = modal.querySelector('input[name="id"]').value;
            const isGiftChecked = giftCheckbox.checked;
            const giftVariantId = isGiftChecked ? giftCheckbox.value : null;

            const cartStatus = await fetch('/cart.js').then((r) => r.json());
            const { properties, bundleId } = quickAddResolveBundleProperties(
              variantId,
              isGiftChecked,
              cartStatus.items
            );

            await quickAddPostMainLineItem(variantId, properties);
            await quickAddRunPostAddHooks(isGiftChecked, bundleId, giftVariantId);
            quickAddOpenCartDrawer();
            modalInstance.hide();
          } catch (error) {
            console.error('[GiftDebug] Error:', error);
          } finally {
            quickAddSetButtonLoading(customBtn, false);
          }
        });
      }

      preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
        if (!variantPicker) return;
        variantPicker.setAttribute('data-update-url', 'false');
      }

      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        this.productElement.querySelectorAll('modal-dialog').forEach((modal) => modal.remove());
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;
        galleryList.setAttribute('role', 'presentation');
        galleryList
          .querySelectorAll('[id^="Slide-"]')
          .forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector('.product');
        if (!product || !product.classList.contains('product--columns')) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let sizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
        if (product.classList.contains('product--medium')) sizes = sizes.replace('715px', '605px');
        else if (product.classList.contains('product--small')) sizes = sizes.replace('715px', '495px');

        mediaImages.forEach((img) => img.setAttribute('sizes', sizes));
      }

      forceModalGalleryImagesLoad() {
        const root = this.modalContent;
        if (!root) return;
        root.querySelectorAll(".product__media img[loading='lazy']").forEach((img) => {
          img.loading = 'eager';
        });
        root.querySelectorAll('.product__media img').forEach((img) => {
          if (img.complete) return;
          const src = img.currentSrc || img.src;
          if (src) img.src = src;
        });
      }

      syncModalVariantMedia() {
        const productRoot = this.modalContent.querySelector('.product');
        const vid = productRoot?.querySelector('input[name="id"]')?.value;
        if (!productRoot || !vid || typeof window.syncVariantMediaVisibility !== 'function') return;
        requestAnimationFrame(() => {
          window.syncVariantMediaVisibility(productRoot, vid);
          setTimeout(() => this.syncThumbnailRailWithMainImage(), 280);
        });
      }

      syncThumbnailRailWithMainImage() {
        if (!this.modalContent) return;
        const product = this.modalContent.querySelector('.product--thumbnail');
        if (!product) return;
        if (!window.matchMedia('(min-width: 1325px)').matches) {
          this.clearThumbnailRailStyles();
          return;
        }

        const activeMedia = product.querySelector(
          '.product__media-item.is-active .product-media-container'
        );
        const list = product.querySelector('.thumbnail-list');
        const arrowDown = product.querySelector('.arrow-down-scroll');
        if (!activeMedia || !list) return;

        const mainH = activeMedia.getBoundingClientRect().height;
        if (mainH < 40) return;

        const listActive = list.classList.contains('list-active');
        const topReserve = listActive ? 65 : 36;
        const bottomReserve = 46;
        const listMax = Math.max(88, Math.round(mainH - topReserve - bottomReserve));

        list.style.maxHeight = `${listMax}px`;
        if (arrowDown) {
          arrowDown.style.top = `${Math.round(topReserve + listMax + 8)}px`;
        }
      }

      setupThumbnailHeightSync() {
        this.teardownThumbnailHeightSync();
        const wrapper = this.modalContent?.querySelector('.product__media-wrapper');
        if (!wrapper) return;

        const sync = () => this.syncThumbnailRailWithMainImage();

        requestAnimationFrame(() => {
          sync();
          setTimeout(sync, 120);
          setTimeout(sync, 400);
        });

        if (typeof ResizeObserver !== 'undefined') {
          this._thumbResizeObs = new ResizeObserver(() => {
            clearTimeout(this._thumbObsDebounce);
            this._thumbObsDebounce = setTimeout(sync, 60);
          });
          this._thumbResizeObs.observe(wrapper);
        }

        this._thumbWindowResize = () => {
          if (window.matchMedia('(min-width: 1325px)').matches) sync();
          else this.clearThumbnailRailStyles();
        };
        window.addEventListener('resize', this._thumbWindowResize);
      }

      teardownThumbnailHeightSync() {
        clearTimeout(this._thumbObsDebounce);
        this._thumbObsDebounce = null;
        if (this._thumbResizeObs) {
          this._thumbResizeObs.disconnect();
          this._thumbResizeObs = null;
        }
        if (this._thumbWindowResize) {
          window.removeEventListener('resize', this._thumbWindowResize);
          this._thumbWindowResize = null;
        }
      }

      clearThumbnailRailStyles() {
        const product = this.modalContent?.querySelector('.product--thumbnail');
        if (!product) return;
        const list = product.querySelector('.thumbnail-list');
        const arrowDown = product.querySelector('.arrow-down-scroll');
        if (list) list.style.maxHeight = '';
        if (arrowDown) arrowDown.style.top = '';
      }
    }
  );
}

if (!window.__quickAddVariantMediaSubscribed) {
  window.__quickAddVariantMediaSubscribed = true;
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const modal = document.querySelector('quick-add-modal[open]');
      if (!modal || !event.data?.variant?.id) return;
      const sid = event.data.sectionId;
      const pickers = modal.querySelectorAll(
        'variant-radios[data-original-section], variant-selects[data-original-section]'
      );
      const picker = [...pickers].find((p) => p.dataset.originalSection === sid);
      if (!picker) return;
      const productRoot = modal.querySelector('.product');
      if (productRoot && typeof window.syncVariantMediaVisibility === 'function') {
        window.syncVariantMediaVisibility(productRoot, String(event.data.variant.id));
      }
      if (typeof modal.syncThumbnailRailWithMainImage === 'function') {
        setTimeout(() => modal.syncThumbnailRailWithMainImage(), 320);
      }
    });
  }
}
